/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { ReactNode } from 'react';

import { CKEditor5Iframe } from '../components/CKEditor5Iframe/CKEditor5Iframe';
import { Installation } from '../components/Installation/Installation';
import { PluginSelection } from '../components/PluginSelection/PluginSelection';
import type { CapabilityParsed, PluginSelectionMode } from '../components/PluginSelection/types';
import { PresetsSelection } from '../components/PresetsSelection/PresetsSelection';
import { SnippetsContainer } from '../components/SnippetsContainer/SnippetsContainer';
import { ToolbarSelection } from '../components/ToolbarSelection/ToolbarSelection';

export type SectionTab = {
	index: number;
	tabName: string;
	slug: string;
	description: string;
	SidebarComponent: ( props: SidebarComponentProps ) => ReactNode;
	MainComponent: ( props: MainComponentProps ) => ReactNode;
};

export type MainComponentProps = {
	locationOrigin: string;
	previewLocation: string;
};

export type SidebarComponentProps = {
	onGroupToggle: ( group: CapabilityParsed ) => void;
	isSelectedPresetModified: boolean;
	activeTab: SectionTab;
	featuresView: PluginSelectionMode;
	onFeaturesViewChange: ( newView: PluginSelectionMode ) => void;
	onTabChange: ( newValue: number ) => void;
};

export type TabName = typeof tabs[ number ][ 'tabName' ];

export const tabs = [
	{
		index: 0,
		tabName: 'Presets',
		slug: 'presets',
		description: 'Select the foundation for your editor\'s functionality.',
		SidebarComponent: PresetsSelection,
		MainComponent: CKEditor5Iframe
	},
	{
		index: 1,
		tabName: 'Features',
		slug: 'features',
		description: 'Choose what features your editor should have.',
		SidebarComponent: PluginSelection,
		MainComponent: CKEditor5Iframe
	},
	{
		index: 2,
		tabName: 'Toolbar',
		slug: 'toolbar',
		description: 'Set up editor toolbars.',
		SidebarComponent: ToolbarSelection,
		MainComponent: CKEditor5Iframe
	},
	{
		index: 3,
		tabName: 'Installation',
		slug: 'installation',
		description: 'Select the best installation method for you, and finish the setup of your editor.',
		SidebarComponent: Installation,
		MainComponent: SnippetsContainer
	}
] as const satisfies ReadonlyArray<SectionTab>;
